import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { cn } from '@shared/lib/cn';

export interface SwipeAction {
  key: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  /** Matches invite form actions: `neutral` = secondary card, `danger` = destructive. */
  variant?: 'danger' | 'neutral' | 'primary';
}

interface SwipeableRowProps {
  /** Actions revealed on the right when the row is swiped left. */
  actions: SwipeAction[];
  children: ReactNode;
  className?: string;
  /** Width in px for each action chip (icon + label, horizontal). */
  actionWidth?: number;
  /** Disable swiping (e.g. row has no actions). */
  disabled?: boolean;
}

const OPEN_THRESHOLD = 0.35;
const DRAG_START_SLOP = 8;
const RAIL_PADDING_X = 8;
const RAIL_GAP = 8;
/** Per-action width — fits icon + short label on one line. */
const DEFAULT_ACTION_WIDTH = 92;

function railWidthFor(actions: SwipeAction[], actionWidth: number): number {
  if (actions.length === 0) return 0;
  return (
    actions.length * actionWidth +
    RAIL_PADDING_X * 2 +
    (actions.length - 1) * RAIL_GAP
  );
}

/**
 * Horizontally swipeable list row. Swipe left to clip the row from the right
 * and reveal action chips styled like the invite Copy / Cancel button pairs.
 */
export function SwipeableRow({
  actions,
  children,
  className,
  actionWidth = DEFAULT_ACTION_WIDTH,
  disabled = false,
}: SwipeableRowProps): JSX.Element {
  const railWidth = railWidthFor(actions, actionWidth);
  const [reveal, setReveal] = useState(0);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const startX = useRef(0);
  const startReveal = useRef(0);
  const axisLocked = useRef<'h' | 'v' | null>(null);
  const activePointer = useRef<number | null>(null);

  const close = useCallback(() => setReveal(0), []);

  const clampReveal = useCallback(
    (value: number) => Math.max(0, Math.min(railWidth, value)),
    [railWidth],
  );

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (disabled || actions.length === 0) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    activePointer.current = event.pointerId;
    startX.current = event.clientX;
    startReveal.current = reveal;
    axisLocked.current = null;
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointer.current !== event.pointerId) return;
    const dx = event.clientX - startX.current;

    if (axisLocked.current === null) {
      if (Math.abs(dx) < DRAG_START_SLOP) return;
      axisLocked.current = 'h';
      setDragging(true);
      containerRef.current?.setPointerCapture(event.pointerId);
    }
    if (axisLocked.current !== 'h') return;
    event.preventDefault();
    setReveal(clampReveal(startReveal.current - dx));
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (activePointer.current !== event.pointerId) return;
    activePointer.current = null;
    if (axisLocked.current === 'h') {
      setReveal((current) =>
        current > railWidth * OPEN_THRESHOLD ? railWidth : 0,
      );
    }
    axisLocked.current = null;
    setDragging(false);
    if (containerRef.current?.hasPointerCapture?.(event.pointerId)) {
      containerRef.current.releasePointerCapture(event.pointerId);
    }
  };

  useEffect(() => {
    if (reveal === 0) return;
    const onDocPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [reveal, close]);

  const open = reveal > 0;

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-hidden rounded-xl touch-pan-y', className)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div
        className="absolute inset-y-0 right-0 flex items-center gap-2 px-2"
        style={{ width: railWidth }}
        aria-hidden={open ? undefined : true}
      >
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            tabIndex={open ? 0 : -1}
            aria-label={action.label}
            onClick={() => {
              action.onClick();
              close();
            }}
            style={{ width: actionWidth }}
            className={cn(
              'flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl px-2.5',
              'text-caption font-semibold shadow-sm transition-transform duration-fast active:scale-[0.97]',
              action.variant === 'danger'
                ? 'bg-danger text-white'
                : action.variant === 'primary'
                  ? 'bg-primary-700 text-white'
                  : 'bg-card text-ink hairline',
            )}
          >
            {action.icon ? (
              <span className="flex shrink-0 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">
                {action.icon}
              </span>
            ) : null}
            <span className="whitespace-nowrap leading-normal">{action.label}</span>
          </button>
        ))}
      </div>

      <div
        className={cn(
          'relative overflow-hidden rounded-xl',
          open && 'shadow-sm',
          !dragging && 'transition-[width] duration-200 ease-out',
        )}
        style={{ width: `calc(100% - ${reveal}px)` }}
      >
        {open ? (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-black/[0.05] to-transparent"
            aria-hidden
          />
        ) : null}
        {children}
      </div>
    </div>
  );
}
